import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getEndpointUptime } from '../../../shared/api/publicApi';
import '../../../styles/features/status/UptimeStats.css';

function getUptimeColor(percentage) {
  if (percentage >= 99.9) return 'var(--status-up)';
  if (percentage >= 99) return 'var(--status-degraded)';
  return 'var(--status-down)';
}

function UptimeStats({ endpointId, timeframe = 30, size = 128 }) {
  const [uptimeData, setUptimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getEndpointUptime(endpointId, timeframe)
      .then((response) => {
        if (cancelled) return;
        setUptimeData(response.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError('Failed to load uptime data');
        console.error(err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpointId, timeframe]);

  if (loading) return <div className="uptime-loading">Loading uptime...</div>;
  if (error) return <div className="uptime-error">{error}</div>;
  if (!uptimeData) return null;

  const uptime = parseFloat(uptimeData.uptime) || 0;
  const chartData = [
    { name: 'Uptime', value: uptime, fill: 'var(--status-up)' },
    { name: 'Downtime', value: Math.max(0, 100 - uptime), fill: 'var(--status-down)' },
  ];

  return (
    <div className="uptime-donut">
      <div className="uptime-donut_ring" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="74%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              isAnimationActive={false}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="uptime-donut_center">
          <strong style={{ color: getUptimeColor(uptime) }}>{uptime.toFixed(2)}%</strong>
          <span>{timeframe}d</span>
        </div>
      </div>

      <div className="kpi-grid kpi-grid--3">
        <div className="kpi-box">
          <span className="kpi-box_value">{uptimeData.ups}</span>
          <span className="kpi-box_label">Up</span>
        </div>
        <div className="kpi-box">
          <span className="kpi-box_value">{uptimeData.total - uptimeData.ups}</span>
          <span className="kpi-box_label">Failed</span>
        </div>
        <div className="kpi-box">
          <span className="kpi-box_value">{uptimeData.total}</span>
          <span className="kpi-box_label">Total</span>
        </div>
      </div>
    </div>
  );
}

export default UptimeStats;
